import { useEffect, useRef, useState } from 'react'

function easeOutExpo (t: number): number {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t)
}

/**
 * Animates a number from its previous value to `target` using easeOutExpo.
 * Respects `prefers-reduced-motion`.
 *
 * @param target   - The value to animate toward.
 * @param duration - Animation duration in ms (default 600).
 */
export function useCountUp (target: number, duration = 600): number {
  const [displayed, setDisplayed] = useState(target)
  const prev = useRef(target)

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced || prev.current === target) {
      setDisplayed(target)
      prev.current = target
      return
    }

    const start = prev.current
    const startTime = performance.now()
    let raf: number

    const tick = (now: number) => {
      const p = Math.min((now - startTime) / duration, 1)
      setDisplayed(start + (target - start) * easeOutExpo(p))
      if (p < 1) {
        raf = requestAnimationFrame(tick)
      } else {
        prev.current = target
      }
    }

    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, duration])

  return displayed
}
